<template>
  <div key="groups">
    <!-- Header -->
    <div class="flex flex-col items-center text-center gap-2 mb-7">
      <h2 class="text-[24px] sm:text-[2.4rem] md:text-[3.2rem] font-semibold">
        {{ t("liveGroups") }}
      </h2>
      <h4 class="text-[1.6rem] font-medium tracking-[-0.48px]">
        {{ t("subtitle") }}
      </h4>
      <button
        class="inline-flex items-center gap-2 rounded-full bg-[#0f1f2e] text-white px-6 py-3 font-semibold hover:opacity-90 hover:-translate-y-px transition"
        @click="emit('create-group')"
      >
        <svg
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="3"
          stroke-linecap="round"
        >
          <line x1="12" y1="5" x2="12" y2="19" />
          <line x1="5" y1="12" x2="19" y2="12" />
        </svg>
        {{ t("createGroup") }}
      </button>
    </div>

    <!-- Search -->
    <!-- <div class="flex justify-center mb-7">
      <div class="relative w-full max-w-[460px]">
        <svg
          class="absolute left-4 top-1/2 -translate-y-1/2 text-[#9ab4c0] pointer-events-none"
          width="15"
          height="15"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
        >
          <circle cx="11" cy="11" r="8" />
          <line x1="21" y1="21" x2="16.65" y2="16.65" />
        </svg>
        <input
          :value="searchQuery"
          class="w-full rounded-full border border-white/95 bg-white/70 backdrop-blur px-10 py-3 text-[13.5px] text-[#1a2e3b] placeholder-[#9ab4c0] shadow-[0_1px_8px_rgba(0,0,0,0.04)] outline-none focus:border-[rgba(15,31,46,0.2)] focus:shadow-[0_1px_12px_rgba(0,0,0,0.07)] transition"
          :placeholder="t('searchDisease')"
          @input="
            emit(
              'update:searchQuery',
              ($event.target as HTMLInputElement).value,
            )
          "
          @focus="emit('update:showDiseaseDropdown', true)"
          @blur="emit('search-blur')"
        />
        <button
          v-if="selectedDisease"
          class="absolute right-3 top-1/2 -translate-y-1/2 text-[#9ab4c0] hover:text-[#1a2e3b]"
          @click="emit('clear-disease')"
        >
          ✕
        </button>
        <div
          v-if="showDiseaseDropdown && filteredDiseases.length"
          class="absolute top-full mt-1 w-full bg-white border border-black/10 rounded-xl shadow-xl max-h-[250px] overflow-y-auto z-50"
        >
          <div
            v-for="disease in filteredDiseases"
            :key="disease.key"
            class="px-4 py-2 cursor-pointer hover:bg-slate-50"
            @mousedown.prevent="emit('select-disease', disease)"
          >
            {{ disease.label }}
          </div>
        </div>
      </div>
    </div> -->

    <!-- Group list -->
    <div
      class="rounded-[18px] overflow-hidden border border-white/90 bg-white/60 backdrop-blur-2xl shadow-[0_2px_24px_rgba(0,0,0,0.05)]"
    >
      <div
        v-for="group in filteredGroups"
        :key="group.id"
        class="flex items-center gap-4 px-6 py-5 border-b border-black/5 last:border-b-0 cursor-pointer transition-all duration-200 hover:bg-white/60 hover:translate-x-1 hover:shadow-[-4px_0_0_0_#0f1f2e] group"
        @click="emit('view-group', group)"
      >
        <!-- Dot -->
        <div
          class="w-[7px] h-[7px] rounded-full flex-shrink-0 transition-transform duration-200 group-hover:scale-[1.4]"
          :class="
            memberGroupIds.has(group.id) ? 'bg-[#0f1f2e]' : 'bg-[#c8d8e0]'
          "
        />

        <!-- Info -->
        <div class="flex-1 min-w-0">
          <div class="flex items-center gap-2 mb-[5px]">
            <span
              class="text-[15px] font-semibold text-[#0f1f2e] tracking-tight"
              >{{ group.name }}</span
            >
            <span
              v-if="!group.isPublic"
              class="inline-flex items-center gap-1 text-[10px] font-medium text-[#7a9aaa] bg-slate-100 rounded-full px-2 py-0.5"
            >
              <svg
                width="9"
                height="9"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2.5"
              >
                <rect x="3" y="11" width="18" height="11" rx="2" />
                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
              </svg>
              {{ t("privateGroup") }}
            </span>
          </div>
          <div class="flex items-center gap-3 flex-wrap">
            <span class="flex items-center gap-1 text-xs text-[#7a9aaa]">
              <svg
                width="11"
                height="11"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                class="opacity-55"
              >
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                <circle cx="9" cy="7" r="4" />
              </svg>
              {{ group.memberCount }} {{ t("members") }}
            </span>
            <span class="text-xs text-[#7a9aaa]">{{
              getDiseaseLabel(group.diseaseKey)
            }}</span>
          </div>
        </div>

        <!-- Actions -->
        <div class="flex items-center gap-2 flex-shrink-0">
          <template v-if="memberGroupIds.has(group.id)">
            <button
              class="text-[13px] font-semibold px-4 py-2 rounded-full border border-red-200 text-red-400 hover:border-red-300 hover:bg-red-50 whitespace-nowrap transition"
              @click.stop="emit('leave-group', group)"
            >
              {{ t("leave") }}
            </button>
          </template>
          <button
            v-else
            class="text-[12.5px] font-semibold text-white bg-[#0f1f2e] px-5 py-2 rounded-full whitespace-nowrap transition-opacity duration-200 hover:opacity-80 disabled:opacity-50"
            :disabled="joiningGroupId === group.id"
            @click.stop="emit('join-group', group)"
          >
            {{ joiningGroupId === group.id ? t("joining") : t("join") }}
          </button>
          <svg
            class="text-[#c8d8e0] flex-shrink-0"
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2.5"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <polyline points="9 18 15 12 9 6" />
          </svg>
        </div>
      </div>

      <!-- Join error -->
      <div
        v-if="joinGroupError"
        class="px-6 py-3 text-[13px] text-red-500 bg-red-50 border-t border-red-100"
      >
        {{ joinGroupError }}
      </div>

      <!-- Empty state -->
      <div
        v-if="filteredGroups.length === 0"
        class="px-6 py-10 text-center text-[#7a9aaa]"
      >
        {{ t("noGroups") }}
        <button
          class="ml-1 underline text-[#0f1f2e]"
          @click="emit('create-group')"
        >
          {{ t("createFirst") }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { Group, Disease } from "@/types/domain";
import { useLiveGroups } from "@/components/companionGroups/useLiveGroups";

const props = defineProps<{
  groups: Group[];
  memberGroupIds: Set<string>;
  joiningGroupId: string | null;
  joinGroupError: string | null;
  searchQuery: string;
  showDiseaseDropdown: boolean;
  selectedDisease: Disease | null;
  filteredDiseases: Disease[];
  filteredGroups: Group[];
}>();

const emit = defineEmits<{
  (e: "view-group", group: Group): void;
  (e: "create-group"): void;
  (e: "join-group", group: Group): void;
  (e: "leave-group", group: Group): void;
  (e: "update:searchQuery", value: string): void;
  (e: "update:showDiseaseDropdown", value: boolean): void;
  (e: "select-disease", disease: Disease): void;
  (e: "clear-disease"): void;
  (e: "search-blur"): void;
}>();

const { t, getDiseaseLabel } = useLiveGroups(
  { _skipInit: true },
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  (_e: any, ..._args: any[]) => {},
);
</script>
